import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, useSensor, useSensors, PointerSensor, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export function TreeView({ root, selectedId, onSelect, onMoveUp, onMoveDown, onDelete, onReorder }) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const [collapsed, setCollapsed] = React.useState(() => new Set());

  if(!root) return <div className='p-2 text-[11px] text-slate-500'>{t('common.pagebuilder.emptyTree')}</div>;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint:{ distance:4 } }));

  function handleDragEnd(evt){
    const { active, over } = evt;
    if(!over || active.id === over.id) return;
    // Only sibling reordering for now
    onReorder(active.id, over.id);
  }

  const toggleAll = (expand) => {
    if(expand){
      setCollapsed(new Set());
    }else{
      const set = new Set();
      const walk = (n) => {
        if(n.children?.length){
          set.add(n.id);
          n.children.forEach(walk);
        }
      };
      walk(root);
      setCollapsed(set);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className='border-b px-2 py-1 flex items-center gap-2 text-[11px] bg-background/90'>
        <input
          value={query}
          onChange={e=>setQuery(e.target.value)}
          placeholder='Suche...'
          className='flex-1 h-6 px-2 rounded border border-slate-200 bg-slate-50 text-[11px] focus:outline-none focus:ring-1 focus:ring-sky-400'
        />
        <button
          type='button'
          onClick={()=>toggleAll(false)}
          className='px-1.5 py-0.5 rounded border border-slate-200 text-[10px] text-slate-600 hover:bg-slate-50'
        >
          ▾
        </button>
        <button
          type='button'
          onClick={()=>toggleAll(true)}
          className='px-1.5 py-0.5 rounded border border-slate-200 text-[10px] text-slate-600 hover:bg-slate-50'
        >
          ▸
        </button>
      </div>
      <div className='p-2 text-[11px] space-y-1 overflow-y-auto'>
        <Node
          node={root}
          depth={0}
          {...{selectedId,onSelect,onMoveUp,onMoveDown,onDelete,onReorder}}
          query={query}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
      </div>
    </DndContext>
  );
}

function Node({ node, depth, selectedId, onSelect, onMoveUp, onMoveDown, onDelete, onReorder, query, collapsed, setCollapsed }) {
  const isSel = node.id === selectedId;
  const locked = ['codeRequestForm','codeDisplay'].includes(node.type);
  const hasChildren = node.children && node.children.length>0;
  const isCollapsed = collapsed?.has(node.id);

  const matchesQuery = !query || (node.type + ' ' + (node.props?.internalName || '')).toLowerCase().includes(query.toLowerCase());
  const shouldHide = query && !matchesQuery && !hasChildren;
  if(shouldHide) return null;

  const toggleCollapse = () => {
    if(!hasChildren) return;
    setCollapsed(prev => {
      const next = new Set(prev);
      if(next.has(node.id)) next.delete(node.id); else next.add(node.id);
      return next;
    });
  };
  return (
    <div className={`border rounded-md px-2 pt-1 pb-2 shadow-sm cursor-pointer ${isSel? 'border-sky-400 bg-sky-50/70 ring-1 ring-sky-300' : 'border-transparent bg-transparent hover:bg-slate-50'}`}>
      <Row node={node} {...{isSel,locked,depth,onSelect,onMoveUp,onMoveDown,onDelete,hasChildren,isCollapsed,toggleCollapse}} />
      {hasChildren && !isCollapsed && (
        <div className='pl-3 mt-1 space-y-1'>
          <SortableChildList parent={node} selectedId={selectedId} onSelect={onSelect} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDelete={onDelete} onReorder={onReorder} depth={depth} query={query} collapsed={collapsed} setCollapsed={setCollapsed} />
        </div>
      )}
    </div>
  );
}

function Row({ node, isSel, locked, depth, onSelect, onMoveUp, onMoveDown, onDelete, hasChildren, isCollapsed, toggleCollapse }){
  const label = useMemo(()=>{
    const p = node.props || {};
    const htmlToText = (html)=> typeof html === 'string' ? html.replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').trim() : '';
    switch(node.type){
      case 'heading': return htmlToText(p.text) || t('common.pagebuilder.heading');
      case 'text': return (htmlToText(p.html) || '').slice(0,40) || 'Text';
      case 'button': return htmlToText(p.label) || p.href || 'Button';
      case 'section': return p.internalName || 'Section';
      case 'container': return p.internalName || 'Container';
      case 'card': return p.internalName || 'Card';
      case 'row': return p.internalName || 'Row';
      case 'banner': return p.alt || 'Banner';
      case 'codeRequestForm': return 'Formular (kompakt)';
      case 'codeDisplay': return 'Code Anzeige';
      default: return p.label || p.name || node.type;
    }
  }, [node]);

  const typeBadgeClass = 'inline-flex items-center justify-center rounded-full px-1.5 py-[1px] text-[9px] font-mono uppercase tracking-wide';
  const typeColor = (()=>{
    const layout = ['section','container','row','card'];
    const content = ['heading','text','button','banner','companyLogo','companyBrand'];
    const form = ['codeRequestForm','codeRequestGroup','codeRequestName','codeRequestEmail','codeRequestConsent','codeRequestSubmit'];
    if(layout.includes(node.type)) return 'bg-emerald-500/15 text-emerald-700 border border-emerald-400/60';
    if(form.includes(node.type)) return 'bg-violet-500/15 text-violet-700 border border-violet-400/60';
    if(content.includes(node.type)) return 'bg-sky-500/15 text-sky-700 border border-sky-400/60';
    return 'bg-slate-500/15 text-slate-700 border border-slate-400/60';
  })();

  const icon = (()=>{
    if(['section','container','row','card'].includes(node.type)) return '▤';
    if(['heading','text'].includes(node.type)) return '𝚃';
    if(node.type==='button') return '⏺';
    if(node.type==='banner' || node.type==='companyLogo') return '🖼';
    if(locked) return '🔒';
    return '•';
  })();

  return (
    <div className='flex items-center gap-1'>
      <button
        type='button'
        onClick={toggleCollapse}
        className={`w-4 text-[10px] text-slate-500 flex items-center justify-center ${hasChildren ? 'opacity-100' : 'opacity-0 cursor-default'}`}
      >
        {hasChildren ? (isCollapsed ? '▸' : '▾') : '▸'}
      </button>
      <button
        onClick={()=>onSelect(node.id)}
        className={`flex-1 text-left truncate flex items-center gap-1 ${isSel?'font-semibold text-slate-900':'text-slate-700'}`}
      >
        <span className='w-4 text-[10px] text-slate-500 flex items-center justify-center'>{icon}</span>
        <span className={`${typeBadgeClass} ${typeColor}`}>
          {node.type}
        </span>
        <span className='text-[10px] text-slate-600 truncate max-w-[140px]'>
          {label}
        </span>
      </button>
      <div className='flex gap-1'>
        <button onClick={()=>onMoveUp(node.id)} title='Up' className='text-[9px] px-1 border rounded bg-background hover:bg-slate-100'>↑</button>
        <button onClick={()=>onMoveDown(node.id)} title='Down' className='text-[9px] px-1 border rounded bg-background hover:bg-slate-100'>↓</button>
        {depth>0 && !locked && (
          <button
            onClick={()=>onDelete(node.id)}
            title={t('common.form.deleteTooltip')}
            className='text-[9px] px-1 border rounded bg-background hover:bg-red-50 text-red-600 border-red-200'
          >
            ✕
          </button>
        )}
        {locked && (
          <span
            title='Locked'
            className='text-[9px] px-1 border rounded bg-amber-50 border-amber-400 text-amber-700'
          >
            L
          </span>
        )}
      </div>
    </div>
  );
}

function SortableChildList({ parent, selectedId, onSelect, onMoveUp, onMoveDown, onDelete, onReorder, depth, query, collapsed, setCollapsed }){
  // Detect duplicate IDs (can produce React key warning + DnD issues)
  const seen = new Set();
  const dupes = [];
  for(const c of parent.children){
    if(seen.has(c.id)) dupes.push(c.id); else seen.add(c.id);
  }
  if(dupes.length){
    // eslint-disable-next-line no-console
    console.warn('Duplicate node ids detected in tree (should be unique):', dupes);
  }
  const items = parent.children.map(c=>c.id); // dnd-kit items (still raw ids)
  return (
    <SortableContext items={items} strategy={verticalListSortingStrategy}>
      {parent.children.map((child, idx) => (
        <SortableItem
          key={child.id + '-' + idx}
          node={child}
          depth={depth+1}
          selectedId={selectedId}
          onSelect={onSelect}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDelete={onDelete}
          onReorder={onReorder}
          query={query}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />
      ))}
    </SortableContext>
  );
}

function SortableItem({ node, depth, selectedId, onSelect, onMoveUp, onMoveDown, onDelete, onReorder, query, collapsed, setCollapsed }){
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: node.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging? 0.5: 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className='cursor-grab active:cursor-grabbing'>
      <Node
        node={node}
        depth={depth}
        selectedId={selectedId}
        onSelect={onSelect}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        onDelete={onDelete}
        onReorder={onReorder}
        query={query}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />
    </div>
  );
}
